import React, { useState, useEffect } from 'react';
import Icon from '../components/Icon';
import { useNavigate, useLocation } from 'react-router-dom';
import { companyAPI, authAPI, paymentAPI } from '../services/api';
import { useToast } from '../hooks/useToast';
import { metaEvents } from '../utils/facebookPixel';
import { useAppLogo } from '../hooks/useAppLogo';
import { COMPANY_BASIC_FREE_CAMPAIGN_END_LABEL, isCompanyBasicFreeCampaignActive } from '../utils/campaigns';
import '../styles/OnboardingWizard.css';

const getSystemTheme = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'day';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'night' : 'day';
};

function OnboardingWizard({ user: initialUser, onComplete, onLogin, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [theme, setTheme] = useState(getSystemTheme);
  const { logoUrl: appLogoUrl } = useAppLogo(theme);
  const logoUrl = `${process.env.PUBLIC_URL || ''}/logo.png` || appLogoUrl;

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setTheme(e.matches ? 'night' : 'day');
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);
  
  // Load persisted state from sessionStorage
  const getPersistedStep = () => {
    try {
      const saved = sessionStorage.getItem('onboarding_step');
      if (saved !== null) {
        return parseInt(saved, 10);
      }
    } catch (e) {
      console.log('Could not load step from sessionStorage');
    }
    return 0;
  };

  const getPersistedFormData = () => {
    try {
      const saved = sessionStorage.getItem('onboarding_formData');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Don't persist passwords
        return {
          ...parsed,
          password: '',
          confirmPassword: '',
          logo: null,
        };
      }
    } catch (e) {
      console.log('Could not load formData from sessionStorage');
    }
    return null;
  };

  const [user, setUser] = useState(initialUser);
  const [currentStep, setCurrentStep] = useState(getPersistedStep);
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [subscriptionInviteCode, setSubscriptionInviteCode] = useState('');
  const [subscriptionInviteCodeValid, setSubscriptionInviteCodeValid] = useState(false);
  const [subscriptionInviteCodeError, setSubscriptionInviteCodeError] = useState('');
  const [validatingSubscriptionCode, setValidatingSubscriptionCode] = useState(false);
  const [formData, setFormData] = useState(() => {
    const persisted = getPersistedFormData();
    if (persisted) {
      return {
        ...persisted,
        // Ensure these are always present
        companyAddress: persisted.companyAddress || {
          street: '',
          city: '',
          postcode: '',
          country: '',
        },
      };
    }
    return {
      // User type selection
      userType: null, // 'bike_owner' or 'bike_shop'
      // Registration fields
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      firstname: '',
      surname: '',
      companyName: '',
      // Company details
      companyEmail: '',
      companyPhone: '',
      companyAddress: {
        street: '',
        city: '',
        postcode: '',
        country: '',
      },
      logo: null,
      subscriptionType: null, // 'basic', 'professional', 'enterprise'
      completedSteps: [],
    };
  });

  // Persist step and formData to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem('onboarding_step', currentStep.toString());
    } catch (e) {
      console.log('Could not persist step');
    }
  }, [currentStep]);

  useEffect(() => {
    try {
      // Don't persist passwords
      const toSave = { ...formData, password: '', confirmPassword: '', logo: null };
      sessionStorage.setItem('onboarding_formData', JSON.stringify(toSave));
    } catch (e) {
      console.log('Could not persist formData');
    }
  }, [formData]);

  // Clear persisted state when onboarding completes
  const clearPersistedState = () => {
    try {
      sessionStorage.removeItem('onboarding_step');
      sessionStorage.removeItem('onboarding_formData');
    } catch (e) {
      console.log('Could not clear persisted state');
    }
  };

  const [usernameStatus, setUsernameStatus] = useState({
    valid: null, // null, true, false
    loading: false,
    suggestions: []
  });

  const [emailStatus, setEmailStatus] = useState({
    valid: null, // null, true, false
    loading: false,
    message: ''
  });

  const [planDetailsModal, setPlanDetailsModal] = useState({ open: false, plan: null });
  const isBasicCampaignActive = isCompanyBasicFreeCampaignActive();
  const subscriptionInviteRequired = !isBasicCampaignActive;
  const canShowSubscriptionPlans = !subscriptionInviteRequired || subscriptionInviteCodeValid;

  // Plan details for the info modal
  const planDetails = {
    basic: {
      title: 'Basic Plan',
      subtitle: 'Manual service management for small workshops',
      description: 'The Basic plan is perfect for small bike shops that want to provide digital service records to their customers. All operations are manual - you enter services, bikes, and customers yourself.',
      features: [
        { name: 'Manual Service Logging', description: 'Record services with full details, parts, and pricing' },
        { name: 'Manual Bike Registration', description: 'Add customer bikes to the system manually' },
        { name: 'Customer Management', description: 'Manage customer profiles and their bikes' },
        { name: 'Digital Bike Passport', description: 'Create digital passports for customer bikes' },
        { name: 'Service Receipts', description: 'Generate and store digital service receipts' },
        { name: 'Public Map Listing', description: 'Appear on the public shop finder map' },
        { name: 'Customer Reviews', description: 'Collect and display customer ratings' },
        { name: 'Basic Dashboard', description: 'Overview of shop activity' },
      ],
    },
    professional: {
      title: 'Professional Plan',
      subtitle: 'AI receipt extraction - our most popular plan',
      description: 'The Professional plan lets you photograph receipts and invoices. AI extracts bike frame number, service items, parts, and prices automatically. Add services in seconds instead of manual data entry.',
      features: [
        { name: 'All Basic Features', description: 'Everything in Basic, plus AI tools' },
        { name: 'AI item extraction from photo receipt', description: 'Take a photo of any receipt or invoice – AI extracts service items, parts, prices, and bike info automatically', highlight: true },
        { name: 'Frame Number Matching', description: 'Automatically matches services to bikes by frame number' },
      ],
    },
    enterprise: {
      title: 'Enterprise Plan',
      subtitle: '1 month free trial — full billing integration + complete business suite',
      description: 'The Enterprise plan comes with a 1 month free trial. It connects Frame-Book directly to your existing billing system. Services automatically sync to Square, Stripe, Xero, or other platforms. Plus online booking, team management, marketing tools, and priority support.',
      features: [
        { name: '1 Month Free Trial', description: 'Try all Enterprise features free for 30 days — no charge until the trial ends', highlight: true },
        { name: 'All Professional Features', description: 'Everything in Professional, plus integrations' },
        { name: 'Billing Integration', description: 'Connect to Square, Stripe, Xero, QuickBooks, CitrusLime, Lightspeed' },
        { name: 'Auto Invoice Sync', description: 'Services automatically create invoices in your billing system' },
        { name: 'Online Booking System', description: 'Let customers book appointments 24/7 from your website' },
        { name: 'Smart Technician Assignment', description: 'AI-powered automatic assignment of bookings to the best technician based on skills and workload', highlight: true },
        { name: 'Smart Work-Off Delegation', description: 'Automatically reassign jobs when a technician is unavailable or on time off', highlight: true },
        { name: 'Team Management (50)', description: 'Add technicians with individual logins and schedules' },
        { name: 'Technician Scheduling', description: 'Assign bookings to specific team members' },
        { name: 'Email & SMS Marketing', description: 'Send promotional campaigns to your customers' },
        { name: 'Multi-Location Support', description: 'Manage multiple shop locations from one account' },
        { name: 'Advanced Analytics', description: 'Detailed revenue tracking, trends, and insights' },
        { name: 'API Access', description: 'Full API access for custom integrations' },
        { name: 'Priority Support', description: 'Dedicated support with faster response times' },
        { name: 'SLA Guarantee', description: 'Service level agreement with uptime guarantees' },
      ],
    },
  };

  // Debounce username check
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!formData.username || formData.username.length < 3) {
        setUsernameStatus({ valid: null, loading: false, suggestions: [] });
        return;
      }

      // Don't check if we're just traversing steps and username hasn't changed
      // But here we want real-time feedback

      setUsernameStatus(prev => ({ ...prev, loading: true }));
      try {
        const response = await authAPI.checkUsername(formData.username);
        if (response.data.available) {
          setUsernameStatus({ valid: true, loading: false, suggestions: [] });
        } else {
          setUsernameStatus({
            valid: false,
            loading: false,
            suggestions: response.data.suggestions || []
          });
        }
      } catch (error) {
        console.error("Username check failed", error);
        setUsernameStatus({ valid: null, loading: false, suggestions: [] });
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [formData.username]);

  // Debounce email check
  useEffect(() => {
    const timer = setTimeout(async () => {
      // Basic email format check before API call
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!formData.email || !emailRegex.test(formData.email)) {
        setEmailStatus({ valid: null, loading: false, message: '' });
        return;
      }

      setEmailStatus(prev => ({ ...prev, loading: true }));
      try {
        const response = await authAPI.checkEmail(formData.email);
        if (response.data.available) {
          setEmailStatus({ valid: true, loading: false, message: 'Email is available' });
        } else {
          setEmailStatus({
            valid: false,
            loading: false,
            message: response.data.message || 'This email is already registered'
          });
        }
      } catch (error) {
        console.error("Email check failed", error);
        const errorMsg = error.response?.data?.error || 'Could not verify email';
        setEmailStatus({ valid: null, loading: false, message: errorMsg });
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [formData.email]);

  const totalSteps = formData.userType === 'bike_owner' ? 3 :
    formData.userType === 'bike_shop' ? 6 : 1;

  // Check for payment success in URL params and subscription status
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const step = params.get('step');
    const payment = params.get('payment');
    const error = params.get('error');
    let cancelled = false;

    const waitForWebhookActivation = async () => {
      const expectedPlan = params.get('plan');
      const normalizedExpectedPlan = expectedPlan ? String(expectedPlan).toLowerCase() : null;

      for (let attempt = 0; attempt < 14; attempt += 1) {
        if (cancelled) return;
        try {
          await paymentAPI.syncLemonSqueezySubscription(normalizedExpectedPlan);
        } catch (_syncErr) {
          // Keep polling; provider data can lag immediately after checkout.
        }
        try {
          const response = await paymentAPI.getLemonSqueezySubscription();
          const subscription = response.data?.subscription;
          const companyStatus = response.data?.company?.subscription_status;
          const isActivated = subscription?.status === 'active' || subscription?.status === 'trial'
            || companyStatus === 'active' || companyStatus === 'trial';
          if (isActivated) {
            setCurrentStep(3);
            toast.success('Payment confirmed! Your subscription is now active.');
            window.history.replaceState({}, '', '/onboarding');
            return;
          }
        } catch (pollError) {
          // Ignore transient poll errors while waiting for webhook processing.
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      if (!cancelled) {
        toast.success('Payment received. Subscription activation is in progress.');
        window.history.replaceState({}, '', '/onboarding');
      }
    };

    if (step && (payment === 'processing' || payment === 'success')) {
      setCurrentStep(parseInt(step, 10) || 3);
      toast.success('Payment completed. Verifying subscription...');
      waitForWebhookActivation();
    } else if (payment === 'cancelled') {
      setCurrentStep(2);
      toast.error('Payment was cancelled. Please choose a plan to continue.');
      window.history.replaceState({}, '', '/onboarding');
    } else if (error) {
      toast.error('Payment failed. Please try again.');
      window.history.replaceState({}, '', '/onboarding');
    }

    return () => {
      cancelled = true;
    };
  }, [location, toast]);

  // Check subscription status on mount - skip to completion if already subscribed
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!user) return;

      try {
        const response = await paymentAPI.getSubscription();
        const subscription = response.data?.subscription;
        const companyStatus = response.data?.company?.subscription_status;

        // If subscription is active, skip to completion step
        if (subscription?.status === 'active' || companyStatus === 'active') {
          // Go to "All set!" step (last step)
          const completionStep = Math.max(totalSteps - 1, 0);
          setCurrentStep(completionStep);
        }
      } catch (error) {
        // Ignore errors - user might not have subscription yet
        console.log('No subscription found yet');
      }
    };

    checkSubscriptionStatus();
  }, [user, formData.userType, totalSteps]);

  // Steps vary based on user type (no invitation step)
  const bikeOwnerSteps = [
    { number: 0, title: 'Who are you?', description: 'Account type.' },
    { number: 1, title: 'Register', description: 'Create account.' },
    { number: 2, title: 'All set!', description: 'Ready to start!' },
  ];

  const bikeShopSteps = [
    { number: 0, title: 'Who are you?', description: 'Account type.' },
    { number: 1, title: 'Register', description: 'Create account.' },
    { number: 2, title: 'Subscription', description: 'Choose your plan.' },
    { number: 3, title: 'Upload logo', description: 'Upload a logo (optional).' },
    { number: 4, title: 'Welcome!', description: 'Welcome to Frame-Book!' },
    { number: 5, title: 'All set!', description: 'Ready to start!' },
  ];

  const steps = formData.userType === 'bike_owner' ? bikeOwnerSteps :
    formData.userType === 'bike_shop' ? bikeShopSteps :
      [ { number: 0, title: 'Who are you?', description: 'Choose your account type.' } ];

  useEffect(() => {
    // Recover user type after redirects/refresh if step state was persisted.
    if (!formData.userType && user) {
      const inferredType = user.user_type === 'bike_shop' || user.role === 'company_admin'
        ? 'bike_shop'
        : user.user_type === 'bike_owner'
          ? 'bike_owner'
          : null;
      if (inferredType) {
        setFormData((prev) => ({ ...prev, userType: inferredType }));
      }
    }
  }, [formData.userType, user]);

  useEffect(() => {
    // Guard against invalid persisted step values after redirects/back navigation.
    if (!Number.isInteger(currentStep)) {
      setCurrentStep(0);
      return;
    }
    if (currentStep < 0) {
      setCurrentStep(0);
      return;
    }
    if (currentStep > totalSteps - 1) {
      setCurrentStep(totalSteps - 1);
    }
  }, [currentStep, totalSteps]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('The file size cannot exceed 5MB.');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Only image files can be uploaded.');
        return;
      }
      setFormData((prev) => ({ ...prev, logo: file }));
    }
  };

  const handleSubscriptionSelect = (type) => {
    setFormData((prev) => ({ ...prev, subscriptionType: type }));
  };

  const startCheckout = async (planTier) => {
    if (!user) {
      toast.error('Please complete registration before selecting a plan.');
      return;
    }

    setCheckoutLoading(true);
    try {
      const normalizedPlan = String(planTier || '').toLowerCase();
      const response = await paymentAPI.createCheckoutSession(normalizedPlan);

      if (response.data?.campaign_applied) {
        toast.success(response.data?.message || `Basic plan activated for free campaign until ${COMPANY_BASIC_FREE_CAMPAIGN_END_LABEL}.`);
        setCurrentStep(3);
        return;
      }

      const checkoutUrl = response.data?.checkout_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else if (response.data?.checkout_required === false) {
        toast.success('Plan activated successfully.');
        setCurrentStep(3);
      } else {
        toast.error('Failed to start payment.');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'An error occurred while starting the payment');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleNext = async () => {
    // Step 0: User type selection
    if (currentStep === 0) {
      if (!formData.userType) {
        toast.error('Please select your account type');
        return;
      }
      setCurrentStep(1);
      return;
    }

    // Bike Owner Flow
    if (formData.userType === 'bike_owner') {
      if (currentStep === 1) {
        // Registration for bike owner
        if (!formData.username.trim()) {
          toast.error('Username is required');
          return;
        }
        if (!formData.email.trim()) {
          toast.error('Email is required');
          return;
        }
        if (!formData.password.trim()) {
          toast.error('Password is required');
          return;
        }
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          return;
        }

        if (usernameStatus.valid === false) {
          toast.error('Username is already taken. Please choose another.');
          return;
        }

        // Check if email is already registered
        if (emailStatus.valid === false) {
          toast.error('This email is already registered. Please use a different email or login to your existing account.');
          return;
        }

        // If email check is still loading or hasn't been verified, check it now
        if (emailStatus.valid !== true) {
          setLoading(true);
          try {
            const emailCheckResponse = await authAPI.checkEmail(formData.email);
            if (!emailCheckResponse.data.available) {
              toast.error('This email is already registered. Please use a different email or login to your existing account.');
              setEmailStatus({ valid: false, loading: false, message: emailCheckResponse.data.message || 'Email already registered' });
              setLoading(false);
              return;
            }
          } catch (error) {
            console.error('Email check failed:', error);
            // Continue with registration - backend will catch duplicates
          }
        }

        setLoading(true);
        try {
          const registerData = {
            registration_type: 'user',
            username: formData.username,
            email: formData.email,
            password: formData.password,
            firstname: formData.firstname || undefined,
            surname: formData.surname || undefined,
            user_type: 'bike_owner',
          };

          const response = await authAPI.register(registerData);
          const loggedInUser = response.data.user;
          setUser(loggedInUser);

          metaEvents.completeRegistration({ content_name: 'bike_owner' });

          // Don't call onLogin yet - it would trigger App.js to redirect to dashboard
          // We'll call it only when onboarding is complete

          toast.success('Account created successfully!');
          setCurrentStep(2);
        } catch (error) {
          console.error('Registration error:', error.response?.data || error.message);
          const status = error.response?.status;
          const errorMessage = error.response?.data?.error || 'Registration failed';
          if (status === 429) {
            toast.error('Too many registration attempts. Please wait a few minutes and try again.');
          } else {
            toast.error(errorMessage);
          }
        } finally {
          setLoading(false);
        }
        return;
      }
    }

    // Bike Shop Flow
    if (formData.userType === 'bike_shop') {
      if (currentStep === 1) {
        // Registration for bike shop
        if (!formData.companyName.trim()) {
          toast.error('Company name is required');
          return;
        }
        if (!formData.companyEmail.trim()) {
          toast.error('Company email is required');
          return;
        }
        if (!formData.username.trim()) {
          toast.error('Username is required');
          return;
        }
        if (!formData.email.trim()) {
          toast.error('Your email is required');
          return;
        }
        if (!formData.password.trim()) {
          toast.error('Password is required');
          return;
        }
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          return;
        }

        if (usernameStatus.valid === false) {
          toast.error('Username is already taken. Please choose another.');
          return;
        }

        // Check if email is already registered
        if (emailStatus.valid === false) {
          toast.error('This email is already registered. Please use a different email or login to your existing account.');
          return;
        }

        // If email check is still loading or hasn't been verified, check it now
        if (emailStatus.valid !== true) {
          setLoading(true);
          try {
            const emailCheckResponse = await authAPI.checkEmail(formData.email);
            if (!emailCheckResponse.data.available) {
              toast.error('This email is already registered. Please use a different email or login to your existing account.');
              setEmailStatus({ valid: false, loading: false, message: emailCheckResponse.data.message || 'Email already registered' });
              setLoading(false);
              return;
            }
          } catch (error) {
            console.error('Email check failed:', error);
            // Continue with registration - backend will catch duplicates
          }
        }

        setLoading(true);
        try {
          const registerData = {
            registration_type: 'company',
            username: formData.username,
            email: formData.email,
            password: formData.password,
            firstname: formData.firstname || undefined,
            surname: formData.surname || undefined,
            company_name: formData.companyName,
            company_email: formData.companyEmail && formData.companyEmail.trim() ? formData.companyEmail.trim() : undefined,
            phone: formData.companyPhone && formData.companyPhone.trim() ? formData.companyPhone.trim() : undefined,
            user_type: 'bike_shop',
          };

          if (formData.companyAddress.street) {
            registerData.address = formData.companyAddress;
          }

          const response = await authAPI.register(registerData);
          const loggedInUser = response.data.user;
          setUser(loggedInUser);

          metaEvents.completeRegistration({ content_name: 'bike_shop' });

          // Don't call onLogin yet - it would trigger App.js to redirect to dashboard
          // We'll call it only when onboarding is complete

          toast.success('Registration successful!');
          setCurrentStep(2);
        } catch (error) {
          console.error('Registration error:', error.response?.data || error.message);
          const status = error.response?.status;
          const errorMessage = error.response?.data?.error || 'Registration failed';
          if (status === 429) {
            toast.error('Too many registration attempts. Please wait a few minutes and try again.');
          } else if (status === 400) {
            const errorDetails = error.response?.data?.details;

            if (errorDetails) {
              const formattedErrors = Object.entries(errorDetails)
                .map(([field, messages]) => {
                  const fieldName = field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');
                  const messageList = Array.isArray(messages) ? messages.join(', ') : messages;
                  return `${fieldName}: ${messageList}`;
                })
                .join('; ');
              toast.error(`${errorMessage}: ${formattedErrors}`);
            } else {
              toast.error(errorMessage);
            }
          } else {
            toast.error(errorMessage);
          }
        } finally {
          setLoading(false);
        }
        return;
      }

      if (currentStep === 3) {
        // Save company info before going to subscription (after logo upload step)
        setLoading(true);
        try {
          const companyData = {
            name: formData.companyName,
            email: formData.companyEmail,
            phone: formData.companyPhone || undefined,
            address: formData.companyAddress,
          };

          await companyAPI.updateCompanyInfo(companyData);
          toast.success('Company details saved!');
          setCurrentStep(4);
        } catch (error) {
          toast.error(error.response?.data?.error || 'An error occurred while saving');
        } finally {
          setLoading(false);
        }
        return;
      }

      if (currentStep === 2) {
        // Subscription step - handled by button click, not Next button
        if (!formData.subscriptionType) {
          toast.error('Please select a plan');
          return;
        }
        return;
      }
    }

    if (currentStep < totalSteps - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSkip = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleComplete = async () => {
    clearPersistedState();

    // Keep parent auth state in sync so dashboard opens without forcing re-login.
    if (onLogin) {
      try {
        if (user) {
          await onLogin(user, false);
        } else {
          const meResponse = await authAPI.getCurrentUser();
          if (meResponse?.data?.user) {
            await onLogin(meResponse.data.user, false);
          }
        }
      } catch (error) {
        // If sync fails, navigation still happens and protected routes will decide.
        console.log('Could not synchronize authenticated user on onboarding completion');
      }
    }

    // Mark onboarding completed in parent state before navigation.
    if (onComplete) {
      onComplete();
    }
    navigate('/dashboard');
  };

  const handleBackToLogin = async () => {
    clearPersistedState();
    try {
      if (onLogout) {
        await onLogout();
      } else {
        await authAPI.logout();
      }
    } catch (error) {
      // Even if logout fails, continue navigation to avoid trapping the user.
      console.log('Logout during onboarding back-to-login failed');
    }
    navigate('/login');
  };

  // Render user type selection (Step 0 for all users)
  const renderUserTypeSelection = () => (
    <div className="onboarding-step-content">
      <h2>Welcome to Frame-Book!</h2>
      <p>Choose your account type to get started.</p>

      <div className="user-type-selection" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
        marginTop: '32px'
      }}>
        {/* Bike Owner Option */}
        <div
          className={`user-type-card ${formData.userType === 'bike_owner' ? 'selected' : ''}`}
          onClick={() => handleInputChange('userType', 'bike_owner')}
          style={{
            padding: '32px 24px',
            borderRadius: '16px',
            border: formData.userType === 'bike_owner' ? '3px solid #4A8C18' : '2px solid #e5e7eb',
            cursor: 'pointer',
            transition: 'all 0.2s',
            background: formData.userType === 'bike_owner' ? 'rgba(74, 140, 24, 0.05)' : '#ffffff',
            textAlign: 'center'
          }}
        >
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>
            <Icon name="bike" color={formData.userType === 'bike_owner' ? 'brand' : 'secondary'} darkMode={false} size={48} />
          </div>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#111827',
            marginBottom: '8px'
          }}>I'm a Bike Owner</h3>
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            marginBottom: '16px'
          }}>
            Track your bike service history, find bike shops, and book appointments.
          </p>
          <div style={{
            display: 'inline-block',
            padding: '6px 16px',
            borderRadius: '20px',
            background: '#ecfdf5',
            color: '#065f46',
            fontWeight: '600',
            fontSize: '14px'
          }}>
            FREE Forever
          </div>
        </div>

        {/* Bike Shop Option */}
        <div
          className={`user-type-card ${formData.userType === 'bike_shop' ? 'selected' : ''}`}
          onClick={() => handleInputChange('userType', 'bike_shop')}
          style={{
            padding: '32px 24px',
            borderRadius: '16px',
            border: formData.userType === 'bike_shop' ? '3px solid #4A8C18' : '2px solid #e5e7eb',
            cursor: 'pointer',
            transition: 'all 0.2s',
            background: formData.userType === 'bike_shop' ? 'rgba(74, 140, 24, 0.05)' : '#ffffff',
            textAlign: 'center'
          }}
        >
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>
            <Icon name="shop" color={formData.userType === 'bike_shop' ? 'brand' : 'secondary'} darkMode={false} size={48} />
          </div>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#111827',
            marginBottom: '8px'
          }}>I'm a Bike Shop</h3>
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            marginBottom: '16px'
          }}>
            Manage your workshop, track services, connect with customers.
          </p>
          <div style={{
            display: 'inline-block',
            padding: '6px 16px',
            borderRadius: '20px',
            background: 'rgba(74, 140, 24, 0.1)',
            color: '#3d5a20',
            fontWeight: '600',
            fontSize: '14px'
          }}>
            {isBasicCampaignActive ? `Basic FREE until ${COMPANY_BASIC_FREE_CAMPAIGN_END_LABEL}` : 'From £6.99/month'}
          </div>
        </div>
      </div>
      <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
          Already have an account?
        </p>
        <button
          type="button"
          onClick={handleBackToLogin}
          className="btn btn-secondary"
          style={{ padding: '10px 24px' }}
        >
          Back to Login
        </button>
      </div>
    </div>
  );

  // Render bike owner registration form
  const renderBikeOwnerRegistration = () => (
    <div className="onboarding-step-content">
      <h2>Create Your Free Account</h2>
      <p>Join Frame-Book to track your bike service history.</p>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="firstname">First Name</label>
          <input
            type="text"
            id="firstname"
            value={formData.firstname}
            onChange={(e) => handleInputChange('firstname', e.target.value)}
            placeholder="Your first name"
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label htmlFor="surname">Surname</label>
          <input
            type="text"
            id="surname"
            value={formData.surname}
            onChange={(e) => handleInputChange('surname', e.target.value)}
            placeholder="Your surname"
            className="form-input"
          />
        </div>
      </div>
      <div className="form-group">
        <label htmlFor="username">Username *</label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            id="username"
            value={formData.username}
            onChange={(e) => handleInputChange('username', e.target.value)}
            placeholder="Choose a username"
            className={`form-input ${usernameStatus.valid === true ? 'is-valid' : usernameStatus.valid === false ? 'is-invalid' : ''}`}
            required
            style={{
              borderColor: usernameStatus.valid === true ? '#10b981' : usernameStatus.valid === false ? '#ef4444' : undefined,
              paddingRight: '40px'
            }}
          />
          {usernameStatus.loading && (
            <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>
              <div className="spinner-small" style={{ width: '16px', height: '16px', border: '2px solid #e5e7eb', borderTop: '2px solid #4f46e5', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            </div>
          )}
          {!usernameStatus.loading && usernameStatus.valid === true && (
            <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#10b981' }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            </div>
          )}
          {!usernameStatus.loading && usernameStatus.valid === false && (
            <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#ef4444' }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
            </div>
          )}
        </div>
        {usernameStatus.valid === false && usernameStatus.suggestions.length > 0 && (
          <div className="username-suggestions" style={{ marginTop: '8px', fontSize: '14px' }}>
            <p style={{ color: '#ef4444', marginBottom: '4px' }}>Username taken. Suggestions:</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {usernameStatus.suggestions.map(s => (
                <span
                  key={s}
                  onClick={() => handleInputChange('username', s)}
                  style={{
                    background: '#f3f4f6',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    color: '#4b5563',
                    border: '1px solid #d1d5db'
                  }}
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="form-group">
        <label htmlFor="email">Email *</label>
        <div style={{ position: 'relative' }}>
          <input
            type="email"
            id="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="your@email.com"
            className={`form-input ${emailStatus.valid === true ? 'is-valid' : emailStatus.valid === false ? 'is-invalid' : ''}`}
            required
            style={{
              borderColor: emailStatus.valid === true ? '#10b981' : emailStatus.valid === false ? '#ef4444' : undefined,
              paddingRight: '40px'
            }}
          />
          {emailStatus.loading && (
            <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>
              <div className="spinner-small" style={{ width: '16px', height: '16px', border: '2px solid #e5e7eb', borderTop: '2px solid #4f46e5', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            </div>
          )}
          {!emailStatus.loading && emailStatus.valid === true && (
            <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#10b981' }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            </div>
          )}
          {!emailStatus.loading && emailStatus.valid === false && (
            <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#ef4444' }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
            </div>
          )}
        </div>
        {emailStatus.valid === false && (
          <div style={{ marginTop: '8px', fontSize: '14px', color: '#ef4444' }}>
            {emailStatus.message}
          </div>
        )}
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="password">Password *</label>
          <input
            type="password"
            id="password"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            placeholder="Choose a password"
            className="form-input"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password *</label>
          <input
            type="password"
            id="confirmPassword"
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            placeholder="Confirm password"
            className="form-input"
            required
          />
        </div>
      </div>
    </div>
  );

  // Render bike shop registration form
  const renderBikeShopRegistration = () => (
    <div className="onboarding-step-content">
      <h2>Register Your Bike Shop</h2>
      <p>Create your account to get started with Frame-Book.</p>

      {/* Company Info Section */}
      <h3 className="form-section-title">Company Information</h3>
      <div className="form-group">
        <label htmlFor="companyName">Company Name *</label>
        <input
          type="text"
          id="companyName"
          value={formData.companyName}
          onChange={(e) => handleInputChange('companyName', e.target.value)}
          placeholder="e.g. Budapest Bike Workshop"
          className="form-input"
          required
        />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="companyEmail">Company Email *</label>
          <input
            type="email"
            id="companyEmail"
            value={formData.companyEmail}
            onChange={(e) => handleInputChange('companyEmail', e.target.value)}
            placeholder="info@workshop.com"
            className="form-input"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="companyPhone">Phone Number</label>
          <input
            type="tel"
            id="companyPhone"
            value={formData.companyPhone}
            onChange={(e) => handleInputChange('companyPhone', e.target.value)}
            placeholder="+36 1 234 5678"
            className="form-input"
          />
        </div>
      </div>

      {/* Address Section */}
      <h3 className="form-section-title">Address</h3>
      <div className="form-group">
        <label htmlFor="street">Street and House Number</label>
        <input
          type="text"
          id="street"
          value={formData.companyAddress.street}
          onChange={(e) => handleInputChange('companyAddress.street', e.target.value)}
          placeholder="Main street 1."
          className="form-input"
        />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="city">City</label>
          <input
            type="text"
            id="city"
            value={formData.companyAddress.city}
            onChange={(e) => handleInputChange('companyAddress.city', e.target.value)}
            placeholder="Budapest"
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label htmlFor="postcode">Postcode</label>
          <input
            type="text"
            id="postcode"
            value={formData.companyAddress.postcode}
            onChange={(e) => handleInputChange('companyAddress.postcode', e.target.value)}
            placeholder="1011"
            className="form-input"
          />
        </div>
      </div>
      <div className="form-group">
        <label htmlFor="country">Country</label>
        <input
          type="text"
          id="country"
          value={formData.companyAddress.country}
          onChange={(e) => handleInputChange('companyAddress.country', e.target.value)}
          placeholder="Hungary"
          className="form-input"
        />
      </div>

      {/* User Account Section */}
      <h3 className="form-section-title">Your Account</h3>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="firstname">First Name</label>
          <input
            type="text"
            id="firstname"
            value={formData.firstname}
            onChange={(e) => handleInputChange('firstname', e.target.value)}
            placeholder="Your first name"
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label htmlFor="surname">Surname</label>
          <input
            type="text"
            id="surname"
            value={formData.surname}
            onChange={(e) => handleInputChange('surname', e.target.value)}
            placeholder="Your surname"
            className="form-input"
          />
        </div>
      </div>
      <div className="form-group">
        <label htmlFor="username">Username *</label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            id="username"
            value={formData.username}
            onChange={(e) => handleInputChange('username', e.target.value)}
            placeholder="Choose a username"
            className={`form-input ${usernameStatus.valid === true ? 'is-valid' : usernameStatus.valid === false ? 'is-invalid' : ''}`}
            required
            style={{
              borderColor: usernameStatus.valid === true ? '#10b981' : usernameStatus.valid === false ? '#ef4444' : undefined,
              paddingRight: '40px'
            }}
          />
          {usernameStatus.loading && (
            <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>
              <div className="spinner-small" style={{ width: '16px', height: '16px', border: '2px solid #e5e7eb', borderTop: '2px solid #4f46e5', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            </div>
          )}
          {!usernameStatus.loading && usernameStatus.valid === true && (
            <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#10b981' }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            </div>
          )}
          {!usernameStatus.loading && usernameStatus.valid === false && (
            <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#ef4444' }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
            </div>
          )}
        </div>
        {usernameStatus.valid === false && usernameStatus.suggestions.length > 0 && (
          <div className="username-suggestions" style={{ marginTop: '8px', fontSize: '14px' }}>
            <p style={{ color: '#ef4444', marginBottom: '4px' }}>Username taken. Suggestions:</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {usernameStatus.suggestions.map(s => (
                <span
                  key={s}
                  onClick={() => handleInputChange('username', s)}
                  style={{
                    background: '#f3f4f6',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    color: '#4b5563',
                    border: '1px solid #d1d5db'
                  }}
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="form-group">
        <label htmlFor="email">Your Email *</label>
        <div style={{ position: 'relative' }}>
          <input
            type="email"
            id="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="your@email.com"
            className={`form-input ${emailStatus.valid === true ? 'is-valid' : emailStatus.valid === false ? 'is-invalid' : ''}`}
            required
            style={{
              borderColor: emailStatus.valid === true ? '#10b981' : emailStatus.valid === false ? '#ef4444' : undefined,
              paddingRight: '40px'
            }}
          />
          {emailStatus.loading && (
            <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>
              <div className="spinner-small" style={{ width: '16px', height: '16px', border: '2px solid #e5e7eb', borderTop: '2px solid #4f46e5', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            </div>
          )}
          {!emailStatus.loading && emailStatus.valid === true && (
            <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#10b981' }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            </div>
          )}
          {!emailStatus.loading && emailStatus.valid === false && (
            <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#ef4444' }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
            </div>
          )}
        </div>
        {emailStatus.valid === false && (
          <div style={{ marginTop: '8px', fontSize: '14px', color: '#ef4444' }}>
            {emailStatus.message}
          </div>
        )}
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="password">Password *</label>
          <input
            type="password"
            id="password"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            placeholder="Choose a password"
            className="form-input"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password *</label>
          <input
            type="password"
            id="confirmPassword"
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            placeholder="Confirm password"
            className="form-input"
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStepContent = () => {
    // Step 0: User Type Selection
    if (currentStep === 0) {
      return renderUserTypeSelection();
    }

    // Bike owner flow
    if (formData.userType === 'bike_owner') {
      switch (currentStep) {
        case 1:
          return renderBikeOwnerRegistration();
        case 2:
          return (
            <div className="onboarding-step-content onboarding-complete">
              <div className="complete-icon">
                <Icon name="celebration" color="brand" darkMode={false} size={48} />
              </div>
              <h2>Welcome to Frame-Book!</h2>
              <p>Your free account is ready. Start exploring your bike service history!</p>
              <div className="next-steps">
                <h3>What you can do:</h3>
                <ul>
                  <li>View your bike service history</li>
                  <li>Find bike shops on the map</li>
                  <li>Book service appointments</li>
                  <li>Rate and review bike shops</li>
                </ul>
              </div>
            </div>
          );
        default:
          return null;
      }
    }

    // Bike shop flow: Register(1) → Subscription(2) → Logo(3) → Welcome(4) → All set(5)
    switch (currentStep) {
      case 1:
        return renderBikeShopRegistration();

      case 2:
        // Subscription selection (invitation code is only required when campaign is inactive)
        return (
          <div className="onboarding-step-content">
            {!canShowSubscriptionPlans ? (
              <>
                <h2>Subscription – Invitation code required</h2>
                <p style={{ marginBottom: '16px' }}>To subscribe to a plan, enter your invitation code below.</p>
                <div style={{ maxWidth: '360px', marginBottom: '24px' }}>
                  <input
                    type="text"
                    placeholder="XXXX-XXXX-XXXX"
                    className="form-input"
                    style={{
                      fontSize: '18px',
                      textAlign: 'center',
                      letterSpacing: '2px',
                      textTransform: 'uppercase',
                      padding: '14px 16px',
                      width: '100%',
                      marginBottom: '10px'
                    }}
                    value={subscriptionInviteCode}
                    onChange={(e) => {
                      setSubscriptionInviteCode(e.target.value.toUpperCase().trim());
                      setSubscriptionInviteCodeError('');
                    }}
                  />
                  {subscriptionInviteCodeError && (
                    <div style={{ color: '#dc2626', fontSize: '13px', marginBottom: '8px' }}>{subscriptionInviteCodeError}</div>
                  )}
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!subscriptionInviteCode.trim() || validatingSubscriptionCode}
                    onClick={async () => {
                      if (!subscriptionInviteCode.trim()) return;
                      setValidatingSubscriptionCode(true);
                      setSubscriptionInviteCodeError('');
                      try {
                        await authAPI.validateToken(subscriptionInviteCode.trim());
                        setSubscriptionInviteCodeValid(true);
                      } catch (err) {
                        setSubscriptionInviteCodeError(err.response?.data?.error || 'Invalid or already used invitation code.');
                      } finally {
                        setValidatingSubscriptionCode(false);
                      }
                    }}
                    style={{ width: '100%', padding: '12px' }}
                  >
                    {validatingSubscriptionCode ? 'Checking...' : 'Validate code'}
                  </button>
                </div>
              </>
            ) : (
              <>
            <h2>Choose Your Plan</h2>
            <p>Select the best plan for your workshop. Basic can be activated instantly.</p>

            {isBasicCampaignActive && (
              <div style={{
                background: 'linear-gradient(130deg, #ecfdf5 0%, #dcfce7 45%, #f0fdf4 100%)',
                border: '2px solid rgba(21, 128, 61, 0.35)',
                borderRadius: '14px',
                padding: '16px 20px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <Icon name="campaign" color="success" darkMode={false} size={24} />
                <div>
                  <div style={{ fontWeight: '800', color: '#166534', fontSize: '15px', marginBottom: '3px' }}>
                    Company Campaign: Basic registration is FREE for everyone
                  </div>
                  <div style={{ fontSize: '13px', color: '#15803d', lineHeight: '1.45' }}>
                    Valid until {COMPANY_BASIC_FREE_CAMPAIGN_END_LABEL}. Pick Basic and continue without payment.
                  </div>
                </div>
              </div>
            )}

            <div className="subscription-plans" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
              {/* Basic Plan */}
              <div
                className={`subscription-plan ${formData.subscriptionType === 'basic' ? 'selected' : ''}`}
                onClick={() => handleSubscriptionSelect('basic')}
                style={{
                  padding: '24px',
                  borderRadius: '12px',
                  border: formData.subscriptionType === 'basic' ? '3px solid #4A8C18' : '2px solid #e5e7eb',
                  cursor: 'pointer',
                  background: formData.subscriptionType === 'basic' ? 'rgba(74, 140, 24, 0.05)' : '#ffffff'
                }}
              >
                <div className="plan-header">
                  <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>Basic</h3>
                  <div className="plan-price" style={{ fontSize: '24px', fontWeight: '800', color: '#3d5a20' }}>
                    {isBasicCampaignActive ? (
                      <>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#9ca3af', textDecoration: 'line-through', marginRight: '8px' }}>£6.99</span>
                        £0<span style={{ fontSize: '14px', fontWeight: '400', color: '#6b7280' }}>/month</span>
                      </>
                    ) : (
                      <>
                        £6.99<span style={{ fontSize: '14px', fontWeight: '400', color: '#6b7280' }}>/month</span>
                      </>
                    )}
                  </div>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                    {isBasicCampaignActive ? `Manual service management (free until ${COMPANY_BASIC_FREE_CAMPAIGN_END_LABEL})` : 'Manual service management'}
                  </p>
                </div>
                <ul className="plan-features" style={{ listStyle: 'none', padding: 0, margin: '16px 0', fontSize: '13px' }}>
                  <li style={{ padding: '4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ color: '#4A8C18' }}>✓</span> Manual service logging</li>
                  <li style={{ padding: '4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ color: '#4A8C18' }}>✓</span> Bike passport management</li>
                  <li style={{ padding: '4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ color: '#4A8C18' }}>✓</span> Customer management</li>
                  <li style={{ padding: '4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ color: '#4A8C18' }}>✓</span> Public map listing</li>
                  <li style={{ padding: '4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ color: '#4A8C18' }}>✓</span> Customer reviews</li>
                </ul>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setPlanDetailsModal({ open: true, plan: 'basic' }); }}
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(74, 140, 24, 0.4)',
                    color: '#4A8C18',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    marginTop: '8px'
                  }}
                >
                  More info
                </button>
              </div>

              {/* Professional Plan */}
              <div
                className={`subscription-plan ${formData.subscriptionType === 'professional' ? 'selected' : ''}`}
                onClick={() => handleSubscriptionSelect('professional')}
                style={{
                  padding: '24px',
                  borderRadius: '12px',
                  border: formData.subscriptionType === 'professional' ? '3px solid #4A8C18' : '2px solid #4A8C18',
                  cursor: 'pointer',
                  background: formData.subscriptionType === 'professional' ? 'rgba(74, 140, 24, 0.1)' : 'rgba(74, 140, 24, 0.03)',
                  position: 'relative'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#4A8C18',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '700'
                }}>MOST POPULAR</div>
                <div className="plan-header">
                  <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>Professional</h3>
                  <div className="plan-price" style={{ fontSize: '24px', fontWeight: '800', color: '#3d5a20' }}>
                    £20<span style={{ fontSize: '14px', fontWeight: '400', color: '#6b7280' }}>/month</span>
                  </div>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>AI receipt extraction</p>
                </div>
                <ul className="plan-features" style={{ listStyle: 'none', padding: 0, margin: '16px 0', fontSize: '13px' }}>
                  <li style={{ padding: '4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ color: '#4A8C18' }}>✓</span> All Basic features</li>
                  <li style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', background: 'rgba(74, 140, 24, 0.08)', border: '1px solid rgba(74, 140, 24, 0.25)', borderRadius: '6px' }}><span style={{ color: '#4A8C18' }}>&#10003;</span> AI item extraction from photo receipt<span style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#3d5a20' }}>Key</span></li>
                  <li style={{ padding: '4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ color: '#4A8C18' }}>✓</span> Frame number auto-matching</li>
                </ul>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setPlanDetailsModal({ open: true, plan: 'professional' }); }}
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(74, 140, 24, 0.4)',
                    color: '#4A8C18',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    marginTop: '8px'
                  }}
                >
                  More info
                </button>
              </div>

              {/* Enterprise Plan */}
              <div
                className={`subscription-plan ${formData.subscriptionType === 'enterprise' ? 'selected' : ''}`}
                onClick={() => handleSubscriptionSelect('enterprise')}
                style={{
                  padding: '24px',
                  borderRadius: '12px',
                  border: formData.subscriptionType === 'enterprise' ? '3px solid #667eea' : '2px solid #e5e7eb',
                  cursor: 'pointer',
                  background: formData.subscriptionType === 'enterprise' ? 'rgba(102, 126, 234, 0.05)' : '#ffffff'
                }}
              >
                <div className="plan-header">
                  <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>Enterprise</h3>
                  <div className="plan-price" style={{ fontSize: '24px', fontWeight: '800', color: '#667eea' }}>
                    £80<span style={{ fontSize: '14px', fontWeight: '400', color: '#6b7280' }}>/month</span>
                  </div>
                  <div style={{
                    margin: '6px 0',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '700',
                    color: '#166534',
                    background: 'rgba(22, 163, 74, 0.1)',
                    border: '1px solid rgba(22, 163, 74, 0.3)',
                    display: 'inline-block',
                  }}>
                    1 month free trial
                  </div>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>Billing integration + full suite</p>
                </div>
                <ul className="plan-features" style={{ listStyle: 'none', padding: 0, margin: '16px 0', fontSize: '13px' }}>
                  <li style={{ padding: '4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ color: '#667eea' }}>✓</span> All Professional features</li>
                  <li style={{ padding: '4px 0', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600' }}><span style={{ color: '#667eea' }}>✓</span> Billing integration</li>
                  <li style={{ padding: '4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ color: '#667eea' }}>✓</span> Square, Stripe, Xero sync</li>
                  <li style={{ padding: '4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ color: '#667eea' }}>✓</span> Online booking system</li>
                  <li style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', background: 'rgba(102, 126, 234, 0.08)', border: '1px solid rgba(102, 126, 234, 0.25)', borderRadius: '6px' }}><span style={{ color: '#667eea' }}>&#10003;</span> Smart Technician Assignment</li>
                  <li style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', background: 'rgba(102, 126, 234, 0.08)', border: '1px solid rgba(102, 126, 234, 0.25)', borderRadius: '6px' }}><span style={{ color: '#667eea' }}>&#10003;</span> Smart Work-Off Delegation</li>
                  <li style={{ padding: '4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ color: '#667eea' }}>✓</span> Multi-location & team</li>
                </ul>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setPlanDetailsModal({ open: true, plan: 'enterprise' }); }}
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(102, 126, 234, 0.4)',
                    color: '#667eea',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    marginTop: '8px'
                  }}
                >
                  More info
                </button>
              </div>
            </div>

            {/* Lifetime option - subtle */}
            <div style={{
              marginTop: '24px',
              padding: '16px',
              background: 'rgba(245, 158, 11, 0.05)',
              borderRadius: '8px',
              border: '1px dashed rgba(245, 158, 11, 0.3)',
              textAlign: 'center'
            }}>
              <p style={{ fontSize: '13px', color: '#92400e', margin: 0 }}>
                Looking for lifetime access? <a href="mailto:sales@frame-book.com" style={{ color: '#d97706', fontWeight: '600' }}>Contact us</a> for enterprise solutions.
              </p>
            </div>

            {/* Select button for chosen plan */}
            {formData.subscriptionType && user && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  startCheckout(formData.subscriptionType);
                }}
                className="btn btn-primary"
                style={{ width: '100%', marginTop: '24px', padding: '14px' }}
                disabled={checkoutLoading}
              >
                {checkoutLoading
                  ? 'Processing...'
                  : (isBasicCampaignActive && formData.subscriptionType === 'basic'
                    ? `Activate FREE Basic (${COMPANY_BASIC_FREE_CAMPAIGN_END_LABEL} campaign)`
                    : `Continue to secure checkout (${formData.subscriptionType.charAt(0).toUpperCase() + formData.subscriptionType.slice(1)})`)}
              </button>
            )}

            <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '13px', color: '#6b7280' }}>
              {isBasicCampaignActive
                ? `Basic is activated instantly during the campaign (valid until ${COMPANY_BASIC_FREE_CAMPAIGN_END_LABEL}). Paid plans activate after payment confirmation.`
                : 'Subscription activation is completed after payment webhook confirmation.'}
            </p>
              </>
            )}
          </div>
        );

      case 3:
        {
        const hasLogoFile = formData.logo instanceof File || formData.logo instanceof Blob;
        return (
          <div className="onboarding-step-content">
            <h2>Upload logo</h2>
            <p>Upload a logo for your workshop. It will appear on the customer portal and in emails.</p>
            <div className="logo-upload-area">
              {hasLogoFile ? (
                <div className="logo-preview">
                  <img
                    src={URL.createObjectURL(formData.logo)}
                    alt="Logo preview"
                    className="logo-preview-image"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, logo: null }))}
                    className="btn btn-secondary btn-sm"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <label htmlFor="logo-upload" className="logo-upload-label">
                  <div className="logo-upload-icon">📷</div>
                  <div className="logo-upload-text">
                    <strong>Click to upload logo</strong>
                    <span>or drag and drop a file here</span>
                    <small>PNG, JPG up to 5MB</small>
                  </div>
                  <input
                    type="file"
                    id="logo-upload"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="logo-upload-input"
                  />
                </label>
              )}
            </div>
            <button
              type="button"
              onClick={handleSkip}
              className="btn btn-link"
            >
              Skip for now
            </button>
          </div>
        );
        }

      case 4:
        // Welcome message
        return (
          <div className="onboarding-step-content">
            <h2>Welcome to Frame-Book, {formData.companyName || 'Partner'}!</h2>
            <p>
              Thank you for registering. Your workshop is almost ready!
            </p>
            <div className="onboarding-features">
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Bike passport tracking</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Customer portal</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Service management</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Public map listing</span>
              </div>
            </div>
            <div className="welcome-summary">
              <h3>Your Setup Summary</h3>
              <div className="summary-item">
                <span className="summary-label">Company:</span>
                <span className="summary-value">{formData.companyName}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Plan:</span>
                <span className="summary-value" style={{ textTransform: 'capitalize' }}>{formData.subscriptionType || 'Basic'}</span>
              </div>
              {formData.companyEmail && (
                <div className="summary-item">
                  <span className="summary-label">Email:</span>
                  <span className="summary-value">{formData.companyEmail}</span>
                </div>
              )}
              {formData.companyAddress.city && (
                <div className="summary-item">
                  <span className="summary-label">Location:</span>
                  <span className="summary-value">{formData.companyAddress.city}, {formData.companyAddress.country}</span>
                </div>
              )}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="onboarding-step-content onboarding-complete">
            <div className="complete-icon">
              <Icon name="celebration" color="brand" darkMode={false} size={48} />
            </div>
            <h2>All set!</h2>
            <p>
              Your workshop has been configured successfully. You can now start using Frame-Book.
            </p>
            <div className="next-steps">
              <h3>Next steps:</h3>
              <ul>
                <li>Add your first bike</li>
                <li>Record your first service</li>
                <li>Explore the analytics</li>
                <li>Configure online booking</li>
              </ul>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`onboarding-wizard theme-${theme}`}>
      <div className="onboarding-logo-top">
        <div className="onboarding-logo-shell">
          <div className="onboarding-logo-glow"></div>
          {logoUrl && (
            <img src={logoUrl} alt="Frame-Book logo" className="onboarding-logo-large" />
          )}
        </div>
      </div>
      <div className="onboarding-container">
        <div className="onboarding-header">
          <div className="onboarding-progress">
            {steps.map((step) => (
              <div
                key={step.number}
                className={`progress-step ${currentStep >= step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''
                  }`}
              >
                <div className="progress-step-number">
                  {currentStep > step.number ? (
                    <Icon name="success" color="primary" darkMode={theme === 'night'} size={16} />
                  ) : (
                    step.number + 1
                  )}
                </div>
                <div className="progress-step-label">{step.title}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="onboarding-body">
          {renderStepContent()}
        </div>

        <div className="onboarding-footer">
          {currentStep > 0 && currentStep < totalSteps - 1 && !(formData.userType === 'bike_shop' && currentStep === 2) && (
            <button
              type="button"
              onClick={handlePrevious}
              className="btn btn-secondary"
              disabled={loading}
            >
              Back
            </button>
          )}
          <div className="onboarding-footer-spacer"></div>
          {/* Bike shop subscription step - handled by plan selection button */}
          {formData.userType === 'bike_shop' && currentStep === 2 ? (
            !formData.subscriptionType && (
              <p className="subscription-notice" style={{ fontSize: '14px', color: '#6b7280' }}>
                Select a plan above to continue
              </p>
            )
          ) : currentStep < totalSteps - 1 ? (
            <button
              type="button"
              onClick={handleNext}
              className="btn btn-primary"
              disabled={loading || usernameStatus.loading || emailStatus.loading || (currentStep === 1 && emailStatus.valid === false)}
            >
              {loading ? 'Saving...' :
                usernameStatus.loading ? 'Checking username...' :
                emailStatus.loading ? 'Checking email...' :
                  currentStep === 0 ? 'Next' :
                    currentStep === 1 ? 'Create Account' :
                      'Next'}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleComplete}
              className="btn btn-primary btn-large"
            >
              Go to dashboard
            </button>
          )}
        </div>
      </div>

      {/* Plan Details Modal */}
      {planDetailsModal.open && planDetailsModal.plan && planDetails[planDetailsModal.plan] && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px'
          }}
          onClick={() => setPlanDetailsModal({ open: false, plan: null })}
        >
          <div 
            style={{
              background: theme === 'night' ? '#0a0a1a' : '#ffffff',
              border: theme === 'night' ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.08)',
              borderRadius: '20px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: theme === 'night' ? '0 24px 64px rgba(0, 0, 0, 0.55)' : '0 24px 64px rgba(0, 0, 0, 0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ 
              padding: '24px 24px 16px', 
              borderBottom: theme === 'night' ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #e5e7eb',
              position: 'relative'
            }}>
              <button 
                type="button"
                onClick={() => setPlanDetailsModal({ open: false, plan: null })}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  background: theme === 'night' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6',
                  borderRadius: '50%',
                  fontSize: '20px',
                  color: theme === 'night' ? 'rgba(255, 255, 255, 0.85)' : '#111827',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
              <h2 style={{ fontSize: '1.4rem', fontWeight: '700', margin: '0 0 4px', paddingRight: '40px', color: theme === 'night' ? '#ffffff' : '#111827' }}>
                {planDetails[planDetailsModal.plan].title}
              </h2>
              <p style={{ fontSize: '1rem', color: '#4A8C18', margin: 0, fontWeight: '500' }}>
                {planDetails[planDetailsModal.plan].subtitle}
              </p>
            </div>
            <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
              <p style={{ fontSize: '0.95rem', lineHeight: '1.6', color: theme === 'night' ? 'rgba(255, 255, 255, 0.72)' : '#444', margin: '0 0 20px' }}>
                {planDetails[planDetailsModal.plan].description}
              </p>
              <h4 style={{ fontSize: '1rem', fontWeight: '600', color: theme === 'night' ? '#e8ffd8' : '#1a2812', margin: '0 0 12px' }}>
                Includes:
              </h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {planDetails[planDetailsModal.plan].features.map((feature, i) => (
                  <li key={i} style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-start',
                    marginBottom: '12px',
                    padding: feature.highlight ? '8px 10px' : '0',
                    borderRadius: feature.highlight ? '8px' : '0',
                    background: feature.highlight
                      ? (planDetailsModal.plan === 'enterprise'
                          ? (theme === 'night' ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.08)')
                          : (theme === 'night' ? 'rgba(74, 140, 24, 0.2)' : 'rgba(74, 140, 24, 0.08)'))
                      : 'transparent',
                    border: feature.highlight
                      ? (planDetailsModal.plan === 'enterprise'
                          ? (theme === 'night' ? '1px solid rgba(130, 149, 255, 0.45)' : '1px solid rgba(102, 126, 234, 0.35)')
                          : (theme === 'night' ? '1px solid rgba(217, 249, 157, 0.45)' : '1px solid rgba(74, 140, 24, 0.35)'))
                      : 'none'
                  }}>
                    <span style={{ flexShrink: 0, marginTop: '2px' }}>
                      <Icon
                        name="success"
                        customColor={planDetailsModal.plan === 'enterprise' ? '#8295ff' : '#4A8C18'}
                        size={14}
                      />
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '2px' }}>
                        <strong style={{ fontSize: '0.9rem', color: theme === 'night' ? '#ffffff' : '#1a2812', fontWeight: feature.highlight ? '700' : '600' }}>
                          {feature.name}
                        </strong>
                        {feature.highlight && (
                          <span style={{
                            fontSize: '9px',
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            color: planDetailsModal.plan === 'enterprise' ? '#8295ff' : '#4A8C18',
                            background: planDetailsModal.plan === 'enterprise' ? 'rgba(102, 126, 234, 0.15)' : 'rgba(74, 140, 24, 0.15)',
                            padding: '2px 6px',
                            borderRadius: '4px'
                          }}>
                            Key
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: '0.8rem', color: theme === 'night' ? 'rgba(255, 255, 255, 0.62)' : '#666', margin: 0, lineHeight: '1.4' }}>
                        {feature.description}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div style={{
              padding: '16px 24px 20px',
              borderTop: theme === 'night' ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <button
                type="button"
                onClick={() => {
                  handleSubscriptionSelect(planDetailsModal.plan);
                  setPlanDetailsModal({ open: false, plan: null });
                }}
                className="btn btn-primary"
                style={{ padding: '12px 32px' }}
              >
                Select {planDetails[planDetailsModal.plan].title.split(' ')[0]}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OnboardingWizard;



